import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';

const INITIAL_COLLAPSED_GROUPS = {
    Core: false,
    Content: false,
    Operations: false,
    Business: false,
    Education: false,
    System: false,
};

/**
 * AdminLayout - Reusable admin layout with collapsible sidebar
 * Features:
 * - Collapsible sidebar (desktop) with localStorage persistence
 * - Mobile responsive overlay
 * - Role-based navigation
 * - Active route highlighting
 * - Grouped navigation items
 */
const AdminLayout = ({ children, activeTab: externalActiveTab }) => {
    const { t } = useLanguage();
    const { role } = useAuth();
    const location = useLocation();

    // Sidebar state
    const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
        try {
            return localStorage.getItem('admin_sidebar_collapsed') === 'true';
        } catch {
            return false;
        }
    });
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [collapsedGroups, setCollapsedGroups] = useState(INITIAL_COLLAPSED_GROUPS);

    // Persist sidebar collapsed state
    useEffect(() => {
        try {
            localStorage.setItem('admin_sidebar_collapsed', sidebarCollapsed.toString());
        } catch (e) {
            console.error('Failed to save sidebar state', e);
        }
    }, [sidebarCollapsed]);

    // Close mobile sidebar on route change
    useEffect(() => {
        setSidebarOpen(false);
    }, [location.pathname]);

    // Tab configuration with routes
    const tabs = [
        {
            key: 'control_room',
            label: t('admin.control_room', 'Control Room'),
            icon: '🎯',
            route: '/admin',
            exact: true,
        },
        {
            key: 'analytics',
            label: t('admin.analytics', 'Analytics'),
            icon: '📊',
            route: '/admin/analytics',
        },
        {
            key: 'leads',
            label: t('admin.leads', 'Leads'),
            icon: '🎯',
            route: '/admin/leads',
        },
        {
            key: 'submissions',
            label: t('admin.submissions', 'Submissions'),
            icon: '📋',
            route: '/admin/submissions',
        },
        {
            key: 'wizard',
            label: t('admin.wizard', 'Wizard Exits'),
            icon: '🚪',
            route: '/admin/wizard',
        },
        {
            key: 'countries',
            label: t('admin.countries', 'Countries'),
            icon: '🌍',
            route: '/admin/countries',
        },
        {
            key: 'pathways',
            label: t('admin.pathways', 'Pathways'),
            icon: '🛤️',
            route: '/admin/pathways',
        },
        {
            key: 'packages',
            label: t('admin.packages', 'Packages'),
            icon: '📦',
            route: '/admin/packages',
        },
        {
            key: 'translations',
            label: t('admin.translations', 'Translations'),
            icon: '🌐',
            route: '/admin/translations',
        },
        {
            key: 'cases',
            label: t('admin.cases', 'Cases'),
            icon: '📁',
            route: '/admin/cases', // Actual route
        },
        {
            key: 'documents',
            label: t('admin.documents', 'Documents'),
            icon: '📄',
            route: '/admin/documents',
        },
        {
            key: 'clients',
            label: t('admin.clients', 'Clients'),
            icon: '👥',
            route: '/admin/clients', // Actual route
        },
        {
            key: 'visa_products',
            label: t('admin.visa_products', 'Visa Products'),
            icon: '✈️',
            route: '/admin/visa-products',
        },
        {
            key: 'visa_apps',
            label: t('admin.visa_apps', 'Visa Applications'),
            icon: '📝',
            route: '/admin/visa-apps',
        },
        {
            key: 'agreements',
            label: t('admin.agreements', 'Agreements'),
            icon: '📜',
            route: '/admin/agreements', // Actual route
        },
        {
            key: 'universities',
            label: t('admin.universities', 'Universities'),
            icon: '🏛️',
            route: '/admin/universities',
        },
        {
            key: 'uni_apps',
            label: t('admin.uni_apps', 'University Apps'),
            icon: '🎓',
            route: '/admin/uni-apps',
        },
        {
            key: 'matching',
            label: t('admin.matching', 'Program Matching'),
            icon: '🔍',
            route: '/admin/matching', // Actual route
        },
        {
            key: 'staff',
            label: t('admin.staff', 'Staff'),
            icon: '👨‍💼',
            route: '/admin/staff', // Actual route
        },
        {
            key: 'blog',
            label: t('admin.blog', 'Blog'),
            icon: '✍️',
            route: '/admin/blog', // Actual route
        },
        {
            key: 'wiki',
            label: t('admin.wiki', 'Staff Wiki'),
            icon: '📚',
            route: '/admin/wiki',
        },
    ];

    // Gate tabs by role
    const gatedTabs = tabs.filter((tab) => {
        if (tab.key === 'control_room') {
            return ['manager', 'admin', 'cro'].includes(role);
        }
        if (['countries', 'pathways', 'translations', 'packages'].includes(tab.key)) {
            return ['manager', 'admin'].includes(role);
        }
        if (['leads', 'submissions', 'wizard'].includes(tab.key)) {
            return ['cro', 'manager', 'admin'].includes(role);
        }
        if (tab.key === 'clients') {
            return ['cro', 'visa', 'agreements', 'docs', 'education', 'manager', 'admin'].includes(role);
        }
        if (tab.key === 'staff') {
            return ['manager', 'admin'].includes(role);
        }
        if (tab.key === 'agreements') {
            return ['agreements', 'manager', 'admin'].includes(role);
        }
        if (tab.key === 'universities' || tab.key === 'uni_apps') {
            return ['education', 'manager', 'admin'].includes(role);
        }
        if (tab.key === 'matching') {
            return ['education', 'cro', 'manager', 'admin'].includes(role);
        }
        if (tab.key === 'visa_products' || tab.key === 'visa_apps') {
            return ['visa', 'manager', 'admin'].includes(role);
        }
        if (tab.key === 'cases') {
            return ['visa', 'manager', 'admin', 'cro'].includes(role);
        }
        if (tab.key === 'analytics') {
            return ['manager', 'admin'].includes(role);
        }
        if (tab.key === 'blog') {
            return ['manager', 'admin'].includes(role);
        }
        if (tab.key === 'documents') {
            return ['docs', 'manager', 'admin'].includes(role);
        }
        return true;
    });

    // Determine active tab based on route or external prop
    const activeTab = externalActiveTab || gatedTabs.find(tab => {
        if (tab.exact) {
            return location.pathname === tab.route;
        }
        return location.pathname.startsWith(tab.route);
    })?.key || null;

    const toggleGroup = (group) =>
        setCollapsedGroups((prev) => ({ ...prev, [group]: !prev[group] }));

    // Navigation groups
    const navigationGroups = [
        {
            group: 'Core',
            icon: '🎯',
            keys: ['control_room', 'analytics', 'leads', 'submissions', 'wizard'],
            color: 'text-blue-600'
        },
        {
            group: 'Content',
            icon: '📝',
            keys: ['countries', 'pathways', 'packages', 'translations'],
            color: 'text-green-600'
        },
        {
            group: 'Operations',
            icon: '🗂️',
            keys: ['cases', 'documents', 'clients', 'visa_products', 'visa_apps'],
            color: 'text-purple-600'
        },
        {
            group: 'Business',
            icon: '📊',
            keys: ['agreements'],
            color: 'text-orange-600'
        },
        {
            group: 'Education',
            icon: '🎓',
            keys: ['universities', 'uni_apps', 'matching'],
            color: 'text-indigo-600'
        },
        {
            group: 'System',
            icon: '⚙️',
            keys: ['staff', 'blog', 'wiki'],
            color: 'text-gray-600'
        },
    ];

    return (
        <div className="min-h-screen bg-gray-900 flex">
            {/* Mobile sidebar overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Mobile menu button */}
            <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden fixed top-4 left-4 z-30 w-10 h-10 bg-gray-800 border border-gray-700 rounded-lg flex items-center justify-center text-white hover:bg-gray-700 transition-colors"
            >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
            </button>

            {/* Sidebar */}
            <aside
                className={`
          fixed lg:relative top-0 left-0 h-full bg-gray-800 border-r border-gray-700 z-50
          transition-all duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          ${sidebarCollapsed ? 'lg:w-16' : 'lg:w-64'}
          w-64
        `}
            >
                {/* Sidebar Header */}
                <div className={`p-4 border-b border-gray-700 flex justify-between items-center ${sidebarCollapsed ? 'lg:justify-center' : ''}`}>
                    {!sidebarCollapsed && (
                        <div>
                            <h1 className="text-xl font-bold text-white">
                                {t('admin.dashboard.title', 'Admin')}
                            </h1>
                            <p className="mt-1 text-sm text-gray-400">
                                {t('admin.dashboard.subtitle', 'Dashboard')}
                            </p>
                        </div>
                    )}

                    {/* Close button for mobile, collapse button for desktop */}
                    <button
                        onClick={() => sidebarCollapsed ? setSidebarCollapsed(false) : (window.innerWidth >= 1024 ? setSidebarCollapsed(true) : setSidebarOpen(false))}
                        className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
                    >
                        {sidebarCollapsed ? (
                            <svg className="w-5 h-5 hidden lg:block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                            </svg>
                        ) : (
                            <>
                                {/* Desktop: Left arrow */}
                                <svg className="w-5 h-5 hidden lg:block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                                {/* Mobile: X */}
                                <svg className="w-5 h-5 lg:hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </>
                        )}
                    </button>
                </div>

                {/* Navigation Menu */}
                <nav className="p-2 space-y-1 overflow-y-auto h-[calc(100vh-80px)] scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-gray-800">
                    {navigationGroups.map(({ group, icon, keys, color }) => {
                        const groupTabs = gatedTabs.filter(t => keys.includes(t.key));
                        if (!groupTabs.length) return null;

                        const isCollapsed = collapsedGroups[group];
                        const hasActiveItem = groupTabs.some(tab => tab.key === activeTab);

                        return (
                            <div key={group} className="select-none">
                                {/* Group Header - Hide text when sidebar collapsed */}
                                {!sidebarCollapsed && (
                                    <button
                                        type="button"
                                        onClick={() => toggleGroup(group)}
                                        className={`
                      w-full flex items-center justify-between px-3 py-2 text-sm font-bold
                      transition-all duration-200 rounded-lg
                      ${hasActiveItem && isCollapsed
                                                ? `${color} bg-gray-700 border-l-4 border-current`
                                                : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                                            }
                    `}
                                        aria-expanded={!isCollapsed}
                                    >
                                        <div className="flex items-center gap-2">
                                            <span className="text-lg">{icon}</span>
                                            <span className="text-sm">{group}</span>
                                        </div>
                                        <svg
                                            className={`w-4 h-4 transition-all duration-300 ${isCollapsed ? '' : 'rotate-90'}`}
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </button>
                                )}

                                {/* Group Items */}
                                <div
                                    className={`
                    overflow-hidden transition-all duration-300 ease-in-out
                    ${sidebarCollapsed ? 'max-h-full opacity-100' : (isCollapsed ? 'max-h-0 opacity-0' : 'max-h-96 opacity-100')}
                  `}
                                >
                                    <div className={`${sidebarCollapsed ? 'space-y-1' : 'mt-1 ml-1 space-y-0.5 pl-4 border-l-2 border-gray-700'}`}>
                                        {groupTabs.map((tab) => (
                                            <Link
                                                key={tab.key}
                                                to={tab.route}
                                                className={`
                          flex items-center gap-3 px-3 py-2.5 rounded-md text-left font-medium text-sm
                          transition-all duration-150 group
                          ${sidebarCollapsed ? 'justify-center' : ''}
                          ${activeTab === tab.key
                                                        ? 'bg-blue-600 text-white shadow-sm'
                                                        : 'text-gray-400 hover:bg-gray-700 hover:text-white'
                                                    }
                        `}
                                                title={sidebarCollapsed ? tab.label : ''}
                                            >
                                                <span className={`
                          text-lg transition-transform duration-150
                          ${activeTab === tab.key ? 'scale-110' : 'group-hover:scale-105'}
                        `}>
                                                    {tab.icon}
                                                </span>
                                                {!sidebarCollapsed && <span className="truncate">{tab.label}</span>}
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </nav>
            </aside>

            {/* Main Content Area */}
            <main className={`
        flex-1 overflow-y-auto bg-gray-900
        transition-all duration-300
        ${sidebarCollapsed ? 'lg:ml-0' : 'lg:ml-0'}
      `}>
                {children}
            </main>
        </div>
    );
};

export default AdminLayout;
